import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Plus, Eye } from 'lucide-react';
import { createFileRoute } from '@tanstack/react-router';
import SuperAdminLayout from '@/components/SuperAdminLayout';
import { apiFetch } from '@/utils/api';

interface Organization {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    base_currency: string;
    is_active: boolean;
    created_at: string | null;
    user_count: number;
}

function TenantsPage() {
    const navigate = useNavigate();
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchOrganizations();
    }, []);

    const fetchOrganizations = async () => {
        try {
            const response = await apiFetch('/api/superadmin/organizations');

            if (!response.ok) {
                if (response.status === 401) {
                    navigate({ to: '/superadmin-login' });
                    return;
                }
                throw new Error('Failed to fetch organizations');
            }

            const data = await response.json();
            setOrganizations(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateTenant = () => {
        navigate({ to: '/superadmin/tenants/new' });
    };

    const handleViewTenant = (orgId: string) => {
        navigate({ to: `/superadmin/tenants/${orgId}` });
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    if (isLoading) {
        return (
            <SuperAdminLayout>
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight">Tenants</h1>
                            <p className="text-slate-600 mt-1">Manage organizations and their settings</p>
                        </div>
                    </div>
                    <div className="text-center py-12">
                        <p className="text-slate-600">Loading...</p>
                    </div>
                </div>
            </SuperAdminLayout>
        );
    }

    return (
        <SuperAdminLayout>
            <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Tenants</h1>
                    <p className="text-slate-600 mt-1">Manage organizations and their settings</p>
                </div>
                <Button onClick={handleCreateTenant} className="gap-2">
                    <Plus className="h-4 w-4" />
                    New Tenant
                </Button>
            </div>

            {error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <div className="bg-white rounded-lg border border-slate-200">
                {organizations.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-slate-600 mb-4">No tenants yet</p>
                        <Button onClick={handleCreateTenant} variant="outline">
                            Create the first tenant
                        </Button>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Slug</TableHead>
                                <TableHead>Currency</TableHead>
                                <TableHead>Users</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Created</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {organizations.map((org) => (
                                <TableRow key={org.id}>
                                    <TableCell className="font-medium">{org.name}</TableCell>
                                    <TableCell className="text-slate-600">{org.slug}</TableCell>
                                    <TableCell>{org.base_currency}</TableCell>
                                    <TableCell>{org.user_count}</TableCell>
                                    <TableCell>
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            org.is_active
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-slate-100 text-slate-800'
                                        }`}>
                                            {org.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-slate-600">{formatDate(org.created_at)}</TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleViewTenant(org.id)}
                                            className="gap-2"
                                        >
                                            <Eye className="h-4 w-4" />
                                            View
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>
       </div>
        </SuperAdminLayout>
    );
}

export const Route = createFileRoute('/superadmin/tenants/')({
    component: TenantsPage,
});
